import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import DispatchModeMaster from "../components/masters/DispatchModeMaster";
import DocumentTypeMaster from "../components/masters/DocumentTypeMaster";
import RegisterEntryForm from "../components/RegisterEntryForm";
import { fetchInwards, fetchOutwards, deleteInward, deleteOutward } from "../api/inwardOutwardApi";
import { getCsrfToken } from "../../../public/api/authApi";
import RegisterListTable, {
  getInwardOutwardCellText,
  getInwardOutwardColumnDefs,
  loadInwardOutwardSavedVisibility
} from "../components/RegisterListTable";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../global/directory";
import { INWARD_OUTWARD_PAGE_SIZE } from "../utils/inwardOutwardFormConstants";
import { useInwardOutwardRole } from "../utils/useInwardOutwardRole";
import { INWARD_OUTWARD_REGISTER_SUBMODULE_SLUG } from "../utils/inwardOutwardPermissionSlugs";

import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/inwardOutwardDirectory.css";

const RegisterControlPage = () => {
  const perms = useInwardOutwardRole(INWARD_OUTWARD_REGISTER_SUBMODULE_SLUG);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [lookupsVersion, setLookupsVersion] = useState(0);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [directoryType, setDirectoryType] = useState("inward"); // "inward" or "outward"
  const [directoryFilters, setDirectoryFilters] = useState({
    search: "",
    column: "",
    value: ""
  });
  const [directoryPage, setDirectoryPage] = useState(1);
  const [editingRecord, setEditingRecord] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalInwards: 0,
    totalOutwards: 0,
    todayInwards: 0,
    todayOutwards: 0,
  });
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [columnVisibilityByType, setColumnVisibilityByType] = useState(() => {
    const inwardDefs = getInwardOutwardColumnDefs("inward", { includeCreatedBy: true });
    const outwardDefs = getInwardOutwardColumnDefs("outward", { includeCreatedBy: true });
    const inwardSaved = loadInwardOutwardSavedVisibility("inward");
    const outwardSaved = loadInwardOutwardSavedVisibility("outward");
    const inward = {};
    const outward = {};
    inwardDefs.forEach((c) => {
      inward[c.id] = inwardSaved?.[c.id] ?? c.defaultVisible;
    });
    outwardDefs.forEach((c) => {
      outward[c.id] = outwardSaved?.[c.id] ?? c.defaultVisible;
    });
    return { inward, outward };
  });

  const fetchRecords = useCallback(async () => {
    setLoadingRecords(true);
    setErrorMsg("");
    try {
      if (directoryType === "inward") {
        const data = await fetchInwards({ scope: "control" });
        setRecords(data?.data || []);
      } else {
        const data = await fetchOutwards({ scope: "control" });
        setRecords(data?.data || []);
      }
    } catch (err) {
      setErrorMsg(err.message || "Failed to fetch records.");
    } finally {
      setLoadingRecords(false);
    }
  }, [directoryType]);

  const normalizeLocalDate = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const fetchSummaryStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const [inwardRes, outwardRes] = await Promise.all([
        fetchInwards({ scope: "control" }),
        fetchOutwards({ scope: "control" }),
      ]);
      const inwards = inwardRes?.data || [];
      const outwards = outwardRes?.data || [];
      const today = normalizeLocalDate(new Date());

      setSummaryStats({
        totalInwards: inwards.length,
        totalOutwards: outwards.length,
        todayInwards: inwards.filter((row) => normalizeLocalDate(row.date_received) === today).length,
        todayOutwards: outwards.filter((row) => normalizeLocalDate(row.date_sent) === today).length,
      });
    } catch (err) {
      console.error("Failed to fetch inward/outward summary stats", err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
    fetchSummaryStats();
  }, [fetchRecords, fetchSummaryStats, lookupsVersion]);

  const bumpMasterLookups = useCallback(() => {
    setLookupsVersion((v) => v + 1);
  }, []);
  
  const handleFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setEditingRecord(null);
    fetchRecords();
    fetchSummaryStats();
  };

  const handleRequestEdit = (row) => {
    setEditingRecord(row);
    requestAnimationFrame(() => {
      document
        .getElementById("ms-create")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDelete = (id) => {
    const rec = records.find((m) => m.id === id);
    setRecordToDelete(rec);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete || !perms.canDelete) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      
      if (directoryType === "inward") {
        await deleteInward(recordToDelete.id, csrfToken, { scope: "control" });
      } else {
        await deleteOutward(recordToDelete.id, csrfToken, { scope: "control" });
      }
      
      setSuccessMsg("Record deleted successfully.");
      setShowConfirmModal(false);
      setRecordToDelete(null);
      fetchRecords();
      fetchSummaryStats();
    } catch (err) {
      setErrorMsg(err.message || "Failed to delete record.");
    } finally {
      setIsConfirming(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    ...(perms.canView ? [{ key: "stats", label: "Metrics", targetId: "ms-stats" }] : []),
    ...(perms.canCreate || (editingRecord && perms.canEdit)
      ? [{ key: "create", label: "Create / Edit Entry", targetId: "ms-create" }]
      : []),
    ...(perms.canView ? [{ key: "list", label: "Directory", targetId: "ms-list" }] : []),
    ...(perms.canManageMasters
      ? [{ key: "masters", label: "Master tables", targetId: "ms-masters" }]
      : []),
  ];

  const statsItems = [
    { key: "total-inwards", label: "Total Inwards", value: summaryStats.totalInwards, tone: "teal" },
    { key: "total-outwards", label: "Total Outwards", value: summaryStats.totalOutwards, tone: "mint" },
    { key: "today-inwards", label: "Today Inwards", value: summaryStats.todayInwards, tone: "slate" },
    { key: "today-outwards", label: "Today Outwards", value: summaryStats.todayOutwards, tone: "coral" },
  ];

  const columnDefs = React.useMemo(
    () => getInwardOutwardColumnDefs(directoryType, { includeCreatedBy: true }),
    [directoryType]
  );
  const columnVisibility = columnVisibilityByType[directoryType] || {};
  const setColumnVisibility = (next) => {
    setColumnVisibilityByType((prev) => ({ ...prev, [directoryType]: next }));
  };

  const valueOptions = React.useMemo(() => {
    if (!directoryFilters.column) return [];
    const set = new Set();
    records.forEach((row) => {
      const text = String(getInwardOutwardCellText(directoryFilters.column, row) || "—").trim();
      if (text) set.add(text);
    });
    return Array.from(set).sort().map((v) => ({ value: v, label: v }));
  }, [records, directoryFilters.column]);

  const filteredRecords = React.useMemo(() => {
    const q = directoryFilters.search.trim().toLowerCase();
    const col = directoryFilters.column;
    const val = directoryFilters.value.trim().toLowerCase();
    return records.filter((row) => {
      const matchesSearch = !q || columnDefs.some((c) =>
        String(getInwardOutwardCellText(c.id, row) || "").toLowerCase().includes(q)
      );
      if (!matchesSearch) return false;
      if (!col || !val) return true;
      return String(getInwardOutwardCellText(col, row) || "").toLowerCase() === val;
    });
  }, [records, directoryFilters, columnDefs]);

  React.useEffect(() => {
    setDirectoryPage(1);
  }, [directoryFilters, directoryType]);

  const directoryTotal = filteredRecords.length;
  const directoryTotalPages = Math.max(1, Math.ceil(directoryTotal / INWARD_OUTWARD_PAGE_SIZE));
  const page = Math.min(directoryPage, directoryTotalPages);

  const pagedRecords = React.useMemo(() => {
    const start = (page - 1) * INWARD_OUTWARD_PAGE_SIZE;
    return filteredRecords.slice(start, start + INWARD_OUTWARD_PAGE_SIZE);
  }, [filteredRecords, page]);

  const visibleCols = columnDefs.filter((c) => columnVisibility[c.id]);
  const getExportData = React.useCallback(() => ({
    headers: visibleCols.map((c) => c.label),
    rows: filteredRecords.map((row) => visibleCols.map((c) => getInwardOutwardCellText(c.id, row)))
  }), [filteredRecords, visibleCols]);

  if (perms.permissionsLoading) {
    return (
      <div className="app-screen-guard app-screen-guard--loading">
        <p>Loading access…</p>
      </div>
    );
  }

  if (!perms.canView) {
    return <Navigate to="/user-login" replace />;
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Inwards & Outwards Register</h2>
          <p className="user-builder-hero__sub">
            Record inwards and outwards correspondences, and manage document types and dispatch modes master data.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {perms.canView ? (
          <div
            id="ms-stats"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Metrics</h3>
              <p className="user-builder-panel__hint">
                Overview of inward and outward entry counts.
              </p>
            </div>
            <GlobalMetricCards
              items={statsItems}
              loading={loadingStats}
              ariaLabel="Inward outward quick statistics"
            />
          </div>
          ) : null}

          {(perms.canCreate || (editingRecord && perms.canEdit)) ? (
          <div
            id="ms-create"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Record Entry</h3>
              <p className="user-builder-panel__hint">
                Create a new Inward or Outward register entry, or edit an existing one.
              </p>
            </div>
            <RegisterEntryForm
              lookupsRefreshKey={lookupsVersion}
              onSuccess={handleFormSuccess}
              editingRecord={editingRecord}
              onCancelEdit={() => setEditingRecord(null)}
              defaultRegisterType={directoryType}
              apiScope="control"
            />
          </div>
          ) : null}
          
          {perms.canView ? (
          <div
            id="ms-list"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head io-directory-panel-head">
              <h3 className="user-builder-panel__title">Directory</h3>
              <p className="user-builder-panel__hint">
                Search and manage all inward and outward register entries.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <GlobalButton 
                variant={directoryType === "inward" ? "primary" : "ghost"}
                onClick={() => {
                  setDirectoryType("inward");
                  setDirectoryFilters({ search: "", column: "", value: "" });
                }}
              >
                Inward Records
              </GlobalButton>
              <GlobalButton 
                variant={directoryType === "outward" ? "primary" : "ghost"}
                onClick={() => {
                  setDirectoryType("outward");
                  setDirectoryFilters({ search: "", column: "", value: "" });
                }}
              >
                Outward Records
              </GlobalButton>
            </div>

            <GlobalDirectoryShell
              className="gds-shell--compact io-directory-shell"
              filters={(
                <GlobalDirectoryFiltersSection
                  searchLabel="Search"
                  searchPlaceholder={`Search ${directoryType} records`}
                  searchValue={directoryFilters.search}
                  onSearchChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, search: v }))
                  }
                  columnLabel="Column"
                  columnValue={directoryFilters.column}
                  onColumnChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, column: v, value: "" }))
                  }
                  valueLabel="Value"
                  valueValue={directoryFilters.value}
                  onValueChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, value: v }))
                  }
                  valueOptions={valueOptions}
                  gridColumnDefs={columnDefs}
                  onClear={() => setDirectoryFilters({ search: "", column: "", value: "" })}
                  secondaryStart={(
                    <>
                      <GlobalDirectoryColumnPicker
                        columnDefs={columnDefs}
                        visibility={columnVisibility}
                        onVisibilityChange={setColumnVisibility}
                        storageKey={`girinestham.inwardOutwardList.columnVisibility.${directoryType}`}
                      />
                    </>
                  )}
                  secondaryEnd={perms.canExport ? (
                    <GlobalDirectoryExportMenu
                      getExportData={getExportData}
                      filenameBase={`admin_${directoryType}_register`}
                    />
                  ) : null}
                />
              )}
              grid={(
                <RegisterListTable
                  records={pagedRecords}
                  registerType={directoryType}
                  loading={loadingRecords}
                  onDelete={perms.canDelete ? handleDelete : null}
                  onRequestEdit={perms.canEdit ? handleRequestEdit : undefined}
                  showToolbar={false}
                  showExport={false}
                  includeCreatedBy
                  columnVisibilityOverride={columnVisibility}
                />
              )}
              pagination={(
                <GlobalDirectoryPaginationSection
                  page={page}
                  totalPages={directoryTotalPages}
                  total={directoryTotal}
                  pageSize={INWARD_OUTWARD_PAGE_SIZE}
                  onPageChange={setDirectoryPage}
                  label={`${directoryType} register pagination`}
                />
              )}
            />
          </div>
          ) : null}
          {perms.canManageMasters ? (
          <div
            id="ms-masters"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">
                Master Table Management
              </h3>
              <p className="user-builder-panel__hint">
                Configure core constraints and lookups.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginTop: "2rem",
              }}
            >
              <div id="ms-document-types" style={{ scrollMarginTop: "2rem" }}>
                <DocumentTypeMaster onDirectoryChange={bumpMasterLookups} />
              </div>
              <hr style={{ border: "0", borderTop: "1px solid #e5e7eb" }} />
              <div id="ms-dispatch-modes" style={{ scrollMarginTop: "2rem" }}>
                <DispatchModeMaster onDirectoryChange={bumpMasterLookups} />
              </div>
            </div>
          </div>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Dashboard sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title="Confirm Deletion"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton
              onClick={() => setShowConfirmModal(false)}
              disabled={isConfirming}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isConfirming}
            >
              Confirm Delete
            </GlobalButton>
          </div>
        }
      >
        <p>
          Are you sure you want to delete the record{" "}
          <b>{recordToDelete?.inward_no || recordToDelete?.outward_no}</b>?
        </p>
        <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#dc2626" }}>
          This action cannot be undone.
        </p>
      </GlobalModal>
    </div>
  );
};

export default RegisterControlPage;
